import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getMainImage } from "@/lib/experiences";
import LazyLoad from "../image/LazyLoad";
import { formatImageUrl, getLocationTypeIcon } from "@/lib/general";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { WaytoraExperienceInitial } from "@/types";
import logger from "@/utils/logger";

interface ExperienceItemProps {
  experienceData: WaytoraExperienceInitial;
  onOpenModal: (experienceId: string) => void;
  setSelectedExperience: (experience: WaytoraExperienceInitial) => void;
  addToItinerary: (experience: WaytoraExperienceInitial) => void;
  isFetchingDetails: boolean;
  isLiked: boolean;
  setIsLiked: (experienceId: string) => void;
  isAdmin: boolean;
}

export const ExperienceItem: React.FC<ExperienceItemProps> = ({
  experienceData,
  onOpenModal,
  setSelectedExperience,
  addToItinerary,
  isFetchingDetails,
  isLiked,
  setIsLiked,
  isAdmin,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleMoreInfoClick = () => {
    logger.log("More info clicked:", experienceData);
    setSelectedExperience(experienceData);
    onOpenModal(experienceData.waytora_place_id);
  };

  const handleLike = async () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 3000);
    setIsLiked(experienceData.waytora_place_id);
  };

  const handleAddToDay = () => {
    addToItinerary(experienceData);
  };

  if (!experienceData.images || experienceData.images.length === 0) {
    return null;
  }
  return (
    <div
      key={experienceData.id}
      className="experienceItem flex w-full flex-col items-start rounded-md transition duration-300 ease-in-out"
    >
      <div className="relative w-full">
        <button
          onClick={handleMoreInfoClick}
          className="rounded-md h-48 w-full overflow-hidden relative"
        >
          <LazyLoad
            src={formatImageUrl(getMainImage(experienceData), "high")}
            lowResSrc={formatImageUrl(getMainImage(experienceData), "low")}
            alt={experienceData.name}
            className="w-full h-full"
            objectFit="cover"
          />
        </button>

        <div className="group z-10 absolute right-3 top-2 flex items-center justify-center bg-stone-100 w-[27px] h-[27px] rounded-full">
          <button
            onClick={handleLike}
            className="group relative flex items-center justify-center w-full h-full"
          >
            <AnimatePresence>
              {isLiked && (
                <motion.i
                  key="filled-heart"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="text-[14px] text-red-600 absolute"
                >
                  <HeartIconSolid className="w-4 h-4" />
                </motion.i>
              )}
            </AnimatePresence>
            <motion.div
              animate={isLiked ? { scale: 0 } : { scale: 1 }}
              className={`${
                isLiked ? "text-transparent" : "text-gray-600"
              } transition-colors duration-300`}
            >
              <HeartIcon className="w-4 h-4" />
            </motion.div>
            {isAnimating && (
              <motion.div
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 bg-red-500/75 rounded-full"
              />
            )}
          </button>
        </div>

        <AnimatePresence>
          {isAnimating && (
            <div className="absolute right-3 top-2 w-[35px] h-[35px] overflow-visible pointer-events-none">
              {[...Array(5)].map((_, index) => (
                <motion.i
                  key={index}
                  initial={{
                    opacity: 1,
                    scale: 0,
                    x: "50%",
                    y: "50%",
                  }}
                  animate={{
                    opacity: 0,
                    scale: 1.5,
                    x: `${100 + (Math.random() - 0.5) * 100}%`,
                    y: `${-50 - Math.random() * 100}%`,
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 1,
                    ease: "easeOut",
                    opacity: { duration: 2 },
                  }}
                  className="fa-solid fa-heart text-red-600 text-sm absolute"
                  style={{
                    originX: "50%",
                    originY: "50%",
                  }}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex w-full flex-row items-center justify-between pt-0.5 pb-3">
        <div className="flex flex-col items-start justify-start space-y-1 w-full">
          <div className="w-full">
            <h3 className="experience-name-text text-[15px] w-full font-[500] tracking-tight text-neutral-800 truncate">
              {experienceData.name}
            </h3>
          </div>
          <div className="w-full flex flex-row items-center justify-start space-x-2 pr-1">
            <div className="flex flex-row items-center space-x-2 w-full pt-1">
              {/* location type icon */}
              <i
                className={`fa-solid ${getLocationTypeIcon(
                  experienceData.types[0] || ""
                )} text-gray-600 text-xs`}
              ></i>
              {experienceData.types.slice(0, 2).map((type: string) => (
                <p
                  key={type}
                  className="experience-name-text text-xs bg-neutral-100 px-3 py-1 rounded-full font-light text-neutral-500 truncate whitespace-nowrap"
                >
                  {type}
                </p>
              ))}
            </div>

            {isAdmin && (
              <button
                onClick={handleAddToDay}
                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-indigo-800 hover:bg-indigo-700 font-medium transition-all duration-500 ease-in-out text-white"
              >
                {isFetchingDetails ? (
                  <i className="fa-solid fa-circle-notch text-xs animate-spin"></i>
                ) : (
                  <i className="fa-solid fa-plus text-xs"></i>
                )}
              </button>
            )}

            <button
              onClick={handleMoreInfoClick}
              className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 font-medium transition-all duration-500 ease-in-out text-neutral-800"
            >
              <p className="text-xs font-bold italic transition-transform duration-300 ease-in-out">
                i
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
